import { useState } from 'react';
import { Zap, Server, Bot, ChevronDown, ChevronUp, Wrench } from 'lucide-react';

import pluginData from '../data/plugins.json';

type PluginType = 'skill' | 'mcp' | 'agent';

interface Plugin {
  name: string;
  description: string;
  version: string;
  type: PluginType;
  author: { name: string; email?: string };
  longDescription: string | null;
  features: string[];
  tools: { name: string; description: string }[];
  examples: string[];
  hasMcpServers: boolean;
}

interface BrowsePageProps {
  type: PluginType;
}

const typeConfig = {
  skill: { title: 'Skills', icon: Zap, color: 'primary', textColor: 'text-primary' },
  mcp: { title: 'MCP Servers', icon: Server, color: 'green-500', textColor: 'text-green-400' },
  agent: { title: 'Agents', icon: Bot, color: 'purple-500', textColor: 'text-purple-400' },
};

function PluginCard({ plugin }: { plugin: Plugin }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = typeConfig[plugin.type];
  const hasDetails = plugin.features.length > 0 || plugin.tools.length > 0 || plugin.examples.length > 0 || plugin.longDescription;

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full text-left p-6 ${hasDetails ? 'cursor-pointer hover:bg-card/80' : 'cursor-default'} transition-colors`}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-foreground">{plugin.name}</h2>
              <span className="px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                v{plugin.version}
              </span>
            </div>
            <p className="text-muted-foreground mt-1">{plugin.description}</p>
            {plugin.longDescription && plugin.longDescription !== plugin.description && !expanded && (
              <p className="text-sm text-muted-foreground/70 mt-2 line-clamp-2">
                {plugin.longDescription}
              </p>
            )}
          </div>
          {hasDetails && (
            <div className="ml-4 shrink-0 text-muted-foreground">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-6 pb-6 pt-4 space-y-5">
          {plugin.longDescription && plugin.longDescription !== plugin.description && (
            <p className="text-sm text-muted-foreground">{plugin.longDescription}</p>
          )}

          {plugin.features.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Features</h3>
              <ul className="space-y-1.5">
                {plugin.features.map((f, i) => (
                  <li key={i} className="flex items-start text-sm text-muted-foreground">
                    <span className={`${cfg.textColor} mr-2 mt-0.5`}>
                      <Zap className="w-3.5 h-3.5" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {plugin.tools.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Tools</h3>
              <div className="grid gap-2">
                {plugin.tools.map((t) => (
                  <div key={t.name} className="flex items-start text-sm">
                    <Wrench className={`w-3.5 h-3.5 ${cfg.textColor} mr-2 mt-0.5 shrink-0`} />
                    <span>
                      <code className="font-mono text-foreground">{t.name}</code>
                      <span className="text-muted-foreground ml-2">— {t.description}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plugin.examples.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Example Prompts</h3>
              <div className="bg-background rounded-lg border border-border p-4 space-y-1.5">
                {plugin.examples.map((e, i) => (
                  <p key={i} className="text-sm font-mono text-muted-foreground">
                    "{e}"
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border">
            <span>Author: {plugin.author.name}</span>
            {plugin.hasMcpServers && (
              <span className="flex items-center gap-1">
                <Server className="w-3 h-3" />
                Includes MCP server
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function BrowsePage({ type }: BrowsePageProps) {
  const cfg = typeConfig[type];
  const Icon = cfg.icon;
  const plugins = (pluginData.plugins as Plugin[]).filter((p) => p.type === type);

  return (
    <div className="px-6 lg:px-8 py-12">
      <div className="flex items-center gap-4 mb-8">
        <div className={`p-3 bg-${cfg.color}/15 rounded-lg`}>
          <Icon className={`w-6 h-6 ${cfg.textColor}`} />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground">{cfg.title}</h1>
          <p className="text-sm text-muted-foreground">{plugins.length} package{plugins.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {plugins.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No packages in this category yet.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {plugins.map((plugin) => (
            <PluginCard key={plugin.name} plugin={plugin} />
          ))}
        </div>
      )}
    </div>
  );
}
